import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getEAProject, getQCFindings, getQCReport, updateQCFinding, approveQC, rejectQC } from '../services/ea-api'
import { getClient } from '../lib/aperture'
import type { EAQCFindingListItem, EAQCSeverity } from '../types/ea'
import EAFindingCard from '../components/ea/EAFindingCard'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

type QCVerdict = 'APPROVED' | 'APPROVED-WITH-FINDINGS' | 'REJECTED' | 'PENDING'

const VERDICT_STYLES: Record<QCVerdict, { color: string; bg: string; border: string }> = {
  APPROVED: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)' },
  'APPROVED-WITH-FINDINGS': { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.4)' },
  REJECTED: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.4)' },
  PENDING: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', border: 'rgba(107, 114, 128, 0.4)' },
}

const SEVERITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'med', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'remediation_in_progress', label: 'Remediation' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'accepted_risk', label: 'Accepted Risk' },
  { value: 'escalated', label: 'Escalated' },
]

const SEVERITY_CARD_COLORS: Record<EAQCSeverity, { color: string; icon: string }> = {
  critical: { color: '#ef4444', icon: 'fa-solid fa-circle-exclamation' },
  high: { color: '#f97316', icon: 'fa-solid fa-triangle-exclamation' },
  med: { color: '#f59e0b', icon: 'fa-solid fa-circle-info' },
  low: { color: '#10b981', icon: 'fa-solid fa-circle-check' },
}

export default function EAQCReview() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const [severityFilter, setSeverityFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [vendorFilter, setVendorFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null)
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: project } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: reportData } = useQuery({
    queryKey: ['ea-qc-report', projectId],
    queryFn: () => getQCReport(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: findingsData, isLoading } = useQuery({
    queryKey: ['ea-qc-findings', projectId, severityFilter, statusFilter, vendorFilter],
    queryFn: () =>
      getQCFindings(projectId!, {
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        vendor: vendorFilter !== 'all' ? vendorFilter : undefined,
      }).then((res) => res.data),
    enabled: !!projectId,
  })

  const findings: EAQCFindingListItem[] = Array.isArray(findingsData) ? findingsData : []

  // Derive verdict from report or findings
  const verdict: QCVerdict = useMemo(() => {
    if (reportData && typeof reportData === 'object' && 'verdict' in reportData) {
      const v = (reportData as Record<string, unknown>).verdict as string
      if (v === 'approved') return 'APPROVED'
      if (v === 'approved_with_findings') return 'APPROVED-WITH-FINDINGS'
      if (v === 'rejected') return 'REJECTED'
    }
    return 'PENDING'
  }, [reportData])

  // Severity counts
  const severityCounts = useMemo(() => {
    const counts: Record<EAQCSeverity, number> = { critical: 0, high: 0, med: 0, low: 0 }
    findings.forEach((f) => { counts[f.severity] = (counts[f.severity] || 0) + 1 })
    return counts
  }, [findings])

  // Extract unique vendors and categories
  const availableVendors = useMemo(() => {
    const vendors = new Set<string>()
    findings.forEach((f) => { if (f.vendor) vendors.add(f.vendor) })
    return Array.from(vendors).sort()
  }, [findings])

  const availableCategories = useMemo(() => {
    const cats = new Set<string>()
    findings.forEach((f) => cats.add(f.category))
    return Array.from(cats).sort()
  }, [findings])

  // Client-side category filter (API doesn't support it)
  const filteredFindings = useMemo(() => {
    if (categoryFilter === 'all') return findings
    return findings.filter((f) => f.category === categoryFilter)
  }, [findings, categoryFilter])

  // Mutations
  const updateFindingMutation = useMutation({
    mutationFn: ({ findingId, data }: { findingId: string; data: { status?: string; resolution_notes?: string } }) =>
      updateQCFinding(findingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea-qc-findings', projectId] })
      queryClient.invalidateQueries({ queryKey: ['ea-qc-report', projectId] })
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => approveQC(projectId!),
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'ea_qc_approved', value: 1 })
      setConfirmAction(null)
      setActionMessage({ type: 'success', text: 'QC approved successfully. Deliverables promotion has been triggered.' })
      queryClient.invalidateQueries({ queryKey: ['ea-qc-report', projectId] })
      queryClient.invalidateQueries({ queryKey: ['ea-project', projectId] })
    },
    onError: () => {
      setConfirmAction(null)
      setActionMessage({ type: 'error', text: 'Failed to approve QC. Please try again.' })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: () => rejectQC(projectId!),
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'ea_qc_rejected', value: 1 })
      setConfirmAction(null)
      setActionMessage({ type: 'success', text: 'QC rejected. Remediation tasks have been triggered for open findings.' })
      queryClient.invalidateQueries({ queryKey: ['ea-qc-report', projectId] })
      queryClient.invalidateQueries({ queryKey: ['ea-qc-findings', projectId] })
      queryClient.invalidateQueries({ queryKey: ['ea-project', projectId] })
    },
    onError: () => {
      setConfirmAction(null)
      setActionMessage({ type: 'error', text: 'Failed to reject QC. Please try again.' })
    },
  })

  const handleAcceptRisk = (findingId: string) => {
    updateFindingMutation.mutate({ findingId, data: { status: 'accepted_risk' } })
  }

  const handleResolve = (findingId: string) => {
    updateFindingMutation.mutate({ findingId, data: { status: 'resolved' } })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading QC review...</p>
        </div>
      </div>
    )
  }

  const verdictStyle = VERDICT_STYLES[verdict]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/ea/projects/${projectId}`}
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(100, 180, 255, 0.15)',
              color: 'rgba(148, 163, 184, 0.8)',
            }}
            aria-label="Back to project"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>QC Review</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {project?.project_name || 'Loading...'}
            </p>
          </div>
        </div>
        {/* Global actions */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setConfirmAction('reject')}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center space-x-2"
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#f87171',
            }}
          >
            <i className="fa-solid fa-times-circle"></i>
            <span>Reject QC</span>
          </button>
          <button
            onClick={() => setConfirmAction('approve')}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center space-x-2"
            style={{
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.4)',
              color: '#34d399',
            }}
          >
            <i className="fa-solid fa-check-circle"></i>
            <span>Approve QC</span>
          </button>
        </div>
      </div>

      {/* Action message */}
      {actionMessage && (
        <div
          className="rounded-xl p-4 flex items-center justify-between"
          style={{
            background: actionMessage.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${actionMessage.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          }}
        >
          <div className="flex items-center space-x-3">
            <i
              className={`fa-solid ${actionMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
              style={{ color: actionMessage.type === 'success' ? '#10b981' : '#ef4444' }}
            ></i>
            <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{actionMessage.text}</span>
          </div>
          <button
            onClick={() => setActionMessage(null)}
            className="text-sm"
            style={{ color: 'rgba(148, 163, 184, 0.6)' }}
            aria-label="Dismiss message"
          >
            <i className="fa-solid fa-times"></i>
          </button>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmAction && (
        <div
          className="rounded-xl p-6"
          style={{
            background: confirmAction === 'approve' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
            border: `1px solid ${confirmAction === 'approve' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
          }}
        >
          <div className="flex items-start space-x-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: confirmAction === 'approve' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${confirmAction === 'approve' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              }}
            >
              <i
                className={`fa-solid ${confirmAction === 'approve' ? 'fa-check-double' : 'fa-ban'} text-xl`}
                style={{ color: confirmAction === 'approve' ? '#10b981' : '#ef4444' }}
              ></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1" style={{ color: '#e2e8f0' }}>
                {confirmAction === 'approve' ? 'Approve QC Review?' : 'Reject QC Review?'}
              </h3>
              <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                {confirmAction === 'approve'
                  ? 'This will approve all artifacts and trigger promotion to deliverables. Any open findings with accepted risk will remain noted.'
                  : 'This will reject the QC review and trigger remediation tasks for all open findings. Agents will attempt to resolve the issues automatically.'}
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => confirmAction === 'approve' ? approveMutation.mutate() : rejectMutation.mutate()}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                  style={{
                    background: confirmAction === 'approve' ? '#10b981' : '#ef4444',
                    color: '#ffffff',
                    opacity: approveMutation.isPending || rejectMutation.isPending ? 0.6 : 1,
                  }}
                >
                  {(approveMutation.isPending || rejectMutation.isPending) && (
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  )}
                  {confirmAction === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
                </button>
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    background: 'rgba(100, 116, 139, 0.15)',
                    border: '1px solid rgba(100, 116, 139, 0.3)',
                    color: '#94a3b8',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary header with verdict + severity cards */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <span
              className="px-4 py-2 text-sm font-bold rounded-xl"
              style={{
                background: verdictStyle.bg,
                color: verdictStyle.color,
                border: `1px solid ${verdictStyle.border}`,
              }}
            >
              {verdict}
            </span>
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              {findings.length} total finding{findings.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Severity breakdown cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(Object.keys(SEVERITY_CARD_COLORS) as EAQCSeverity[]).map((sev) => {
            const { color, icon } = SEVERITY_CARD_COLORS[sev]
            const count = severityCounts[sev]
            return (
              <div
                key={sev}
                className="rounded-xl p-4 text-center"
                style={{
                  background: `${color}0a`,
                  border: `1px solid ${color}33`,
                }}
              >
                <i className={`${icon} text-2xl mb-2`} style={{ color }}></i>
                <div className="text-2xl font-bold mb-0.5" style={{ color }}>{count}</div>
                <div className="text-xs font-medium uppercase tracking-wider" style={{ color: `${color}cc` }}>
                  {sev === 'med' ? 'Medium' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Filters:
            </span>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by severity"
            >
              {SEVERITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {availableVendors.length > 0 && (
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(100, 180, 255, 0.15)',
                  color: '#e2e8f0',
                }}
                aria-label="Filter by vendor"
              >
                <option value="all">All Vendors</option>
                {availableVendors.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            )}
            {availableCategories.length > 0 && (
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-xs"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(100, 180, 255, 0.15)',
                  color: '#e2e8f0',
                }}
                aria-label="Filter by category"
              >
                <option value="all">All Categories</option>
                {availableCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
          <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
            {filteredFindings.length} finding{filteredFindings.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Findings list */}
      {filteredFindings.length > 0 ? (
        <div className="space-y-4">
          {filteredFindings.map((finding) => (
            <EAFindingCard
              key={finding.id}
              finding={finding}
              onAcceptRisk={() => handleAcceptRisk(finding.id)}
              onResolve={() => handleResolve(finding.id)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <i
              className="fa-solid fa-clipboard-check text-3xl"
              style={{ color: 'rgba(16, 185, 129, 0.5)' }}
            ></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {severityFilter !== 'all' || statusFilter !== 'all' || vendorFilter !== 'all' || categoryFilter !== 'all'
              ? 'No findings match your filters'
              : 'No QC findings'}
          </h3>
          <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {severityFilter !== 'all' || statusFilter !== 'all' || vendorFilter !== 'all' || categoryFilter !== 'all'
              ? 'Try adjusting your filter criteria'
              : 'QC findings will appear here once the QC pipeline tasks complete'}
          </p>
        </div>
      )}
    </div>
  )
}
